import * as THREE from "three";
import {
  DEFAULT_STATION_ACTIVATION_RADIUS,
  type WorkshopStation,
} from "./WorkshopStation";

/**
 * Owns registered workshop stations: proximity activation, per-frame updates,
 * and station-scoped reset. Does not enforce a linear course between stations.
 *
 * Reset semantics (foundation choice):
 * - There is no global "reset whole workshop" path.
 * - Prefer {@link resetActiveOrNearest} (KeyR) or each station's own reset plate.
 * - Session start/end calls {@link releaseAllSessionHolds} only — locked outlet
 *   wires may persist across re-enter until that station's reset.
 */
export class Workshop {
  private readonly stations = new Map<string, WorkshopStation>();
  private readonly _stationPos = new THREE.Vector3();
  private activeId: string | null = null;

  register(station: WorkshopStation): void {
    if (this.stations.has(station.id)) {
      console.warn(`[Workshop] replacing station "${station.id}"`);
    }
    this.stations.set(station.id, station);
  }

  get(id: string): WorkshopStation | undefined {
    return this.stations.get(id);
  }

  list(): readonly WorkshopStation[] {
    return [...this.stations.values()];
  }

  getActive(): WorkshopStation | null {
    return this.activeId ? (this.stations.get(this.activeId) ?? null) : null;
  }

  /**
   * Nearest station by horizontal distance to player (y ignored).
   * Returns null when the registry is empty.
   */
  getNearest(playerWorldPos: THREE.Vector3): WorkshopStation | null {
    let best: WorkshopStation | null = null;
    let bestDist = Infinity;
    for (const station of this.stations.values()) {
      this.anchorWorldPos(station);
      const dx = this._stationPos.x - playerWorldPos.x;
      const dz = this._stationPos.z - playerWorldPos.z;
      const d = Math.hypot(dx, dz);
      if (d < bestDist) {
        bestDist = d;
        best = station;
      }
    }
    return best;
  }

  private anchorWorldPos(station: WorkshopStation): void {
    const anchor = station.activationAnchor ?? station.root;
    anchor.getWorldPosition(this._stationPos);
  }

  /** Reset one station by id without touching others. */
  resetStation(id: string): void {
    const station = this.stations.get(id);
    if (!station) {
      console.warn(`[Workshop] resetStation: unknown id "${id}"`);
      return;
    }
    station.reset();
  }

  /**
   * KeyR entry: reset the currently active (in-radius) station, else the
   * nearest station. Never resets every station at once.
   */
  resetActiveOrNearest(playerWorldPos: THREE.Vector3): void {
    const target = this.getActive() ?? this.getNearest(playerWorldPos);
    target?.reset();
  }

  /** Drop holds across all grab systems (session boundaries). */
  releaseAllSessionHolds(): void {
    for (const station of this.stations.values()) {
      station.releaseSessionHolds();
    }
  }

  update(dt: number, playerWorldPos: THREE.Vector3): void {
    let nearestInRadius: WorkshopStation | null = null;
    let nearestDist = Infinity;

    for (const station of this.stations.values()) {
      this.anchorWorldPos(station);
      const dx = this._stationPos.x - playerWorldPos.x;
      const dz = this._stationPos.z - playerWorldPos.z;
      const dist = Math.hypot(dx, dz);
      const radius = station.activationRadius ?? DEFAULT_STATION_ACTIVATION_RADIUS;
      const inRadius = dist <= radius;

      if (inRadius && dist < nearestDist) {
        nearestDist = dist;
        nearestInRadius = station;
      }

      station.update(dt, playerWorldPos);
    }

    const nextId = nearestInRadius?.id ?? null;
    if (nextId !== this.activeId) {
      const prev = this.activeId ? this.stations.get(this.activeId) : undefined;
      if (prev) {
        prev.active = false;
        prev.hideIntro?.();
      }
      this.activeId = nextId;
      if (nearestInRadius) {
        nearestInRadius.active = true;
        nearestInRadius.showIntro?.();
      }
    } else if (nearestInRadius) {
      nearestInRadius.active = true;
    }
  }
}
